import React, { Component, PropTypes } from 'react'
import { connect } from 'react-redux'
import Loader from 'react-loader'
import CSSModules from 'react-css-modules'
import { Row, Col } from 'react-flexbox-grid'
import container from '../../helpers/container'
import format from '../../helpers/format'
import convert from '../../helpers/convert'
import analytics from '../../helpers/analytics'
import config from '../../config'
import {
  ContainerDivs,
  Chart,
  Grade,
  Link,
  LinkIcon,
  Tweeter,
  FlaggedFund,
  FamilyGrid,
  GetStartedInput,
  AboutBanner,
  SocialButtons,
  // AssetManagerLetter
} from '../../components'
import * as searchActions from 'redux/modules/search'
const Styles = require('./Family.css')


/**
 * <FamilyResults
 *   fundFamily
 * />
 *
 * Displays results for major fund families. Includes a chart, category
 * breakdowns, tweet button, and stats on ESG and fossil-free funds. Category
 * breakdowns are rendered by <FamilyResultsSectorDetails />.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyResults extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundFamily: props.fundFamily || null
    }
  }

  getChartData = (fam) => {
    const chartFossil = '#333'
    const chartGray = '#D5D5D5'
    return [
      {
        value: fam.ff_all_asset_sum,
        label: 'Fossil fuel stocks: '+format.perc(fam.ff_all_weight_total),
        color: chartFossil
      },
      {
        value: fam.equity_fund_net_assets - fam.ff_all_asset_sum,
        label: 'All other holdings',
        color: chartGray
      }
    ]
  }

  render() {
    const { fundFamily } = this.state
    if (!fundFamily) { return null }

    // styles
    const sriGreen = fundFamily.num_e_sri > 0 ? '-green' : '-black'
    const badgersGreen = fundFamily.num_five_badgers > 0 ? '-green' : '-black'

    // urls
    const safeName = encodeURIComponent(fundFamily.fund_family_label)
    const urlBase = config.routes.list + '?ff=' + safeName
    const sriUrl = urlBase + '&dsc=false&sri=1&srt=grade_fossil'
    const badgerUrl = urlBase + '&fffgrd=1&srt=ussif'

    return (
      <div>

        <div styleName='family-cta'>
          <b>Major fund managers are pouring trillions into fossil fuel
          investments.</b><br/>Share to spread the word that business as
          usual is fueling climate destruction.
        </div>
        <Tweeter
          type={'family'}
          item={ fundFamily }
        />
        <div styleName='family-cta-social-buttons-wrap'>
          <SocialButtons
            message={config.app.title+' - '+fundFamily.name}
            link={config.routes.families+'?q='+this.state.family}
          />
        </div>

        <div styleName='chart-content-hl-wrap'>
          <h2 styleName='chart-content-hl'>
            Equity funds
          </h2>
          <p>
            <LinkIcon faclass='fa-info-circle' nospace />&nbsp;
            This data covers open-end mutual funds and exchange-traded funds
            domiciled in the
            U.S. with at least 50% of assets invested in stocks. It
            excludes bond funds, target date funds, and other investment vehicles
            like closed-end funds, collective trusts, and funds domiciled
            outside the U.S. <b>This asset manager may have additional
            fossil fuel investments in those excluded funds.</b>
          </p>
        </div>
        <Row>
          <Col xs={12} sm={12} md={4} styleName='chart-sidebar-wrap'>

            <Chart
              currency={'US Dollar'}
              invested={convert.int(fundFamily.ff_all_asset_sum)}
              chartData={this.getChartData(fundFamily)}
            />

            <FlaggedFund
              isFamily
              totalFunds={convert.int(fundFamily.num_equity_funds)}
              count={convert.int(fundFamily.ff_all_count_sum)}
              weight={convert.float(fundFamily.ff_all_weight_total)}
            />

          </Col>
          <Col xs={12} sm={12} md={8} styleName='main-content-wrap'>
            <div styleName='sector-details-info-label-energy'>
              <strong>Producers</strong> <small><i>energy sector</i></small>
            </div>

            <FamilyResultsSectorDetails
              type='c2'
              label='Top 200 owners of carbon reserves'
              sublabel='Carbon Underground 200™'
              count={fundFamily.c2_count_sum}
              weight={fundFamily.c2_weight_total}
              asset={fundFamily.c2_asset_sum}
              currency='US Dollar'
            />

            <FamilyResultsSectorDetails
              type='co'
              label='Coal industry'
              sublabel='Coal miners, coal services, owners of coal reserves'
              count={fundFamily.co_count_sum}
              weight={fundFamily.co_weight_total}
              asset={fundFamily.co_asset_sum}
              currency='US Dollar'
            />

            <FamilyResultsSectorDetails
              type='og'
              label='Oil/Gas industry'
              sublabel='Oil/gas producers and refiners, oil field services and equipment companies, and pipeline operators'
              count={fundFamily.og_count_sum}
              weight={fundFamily.og_weight_total}
              asset={fundFamily.og_asset_sum}
              currency='US Dollar'
            />

            <div styleName='sector-details-info-label-utility'>
              <strong>Consumers</strong> <small><i>utility sector</i></small>
            </div>

            <FamilyResultsSectorDetails
              type='f5'
              label='Top 50 coal-fired utilities'
              sublabel='Macroclimate® 50'
              count={fundFamily.mc50_count_sum}
              weight={fundFamily.mc50_weight_total}
              asset={fundFamily.mc50_asset_sum}
              currency='US Dollar'
            />

            <FamilyResultsSectorDetails
              type='ut'
              label='Fossil-fired utilities'
              sublabel='Utilities burning fossil fuels or distributing gas'
              count={fundFamily.ut_count_sum}
              weight={fundFamily.ut_weight_total}
              asset={fundFamily.ut_asset_sum}
              currency='US Dollar'
            />

            <div styleName='sector-details-info'>
              For each fossil
              fuel category, we show the combined exposure of the asset
              manager's U.S.-based equity funds.
            </div>

          </Col>
        </Row>

        <div styleName='good-fund-counts-row'>
          <div styleName='good-fund-counts-col'>
            <Row>
              <Col xs={12} sm={8} md={9}>
                <div styleName='good-fund-counts-title'>
                  Funds with sustainability mandates
                </div>
                <div styleName='good-fund-counts-info'>
                  Number of {fundFamily.fund_family_label} funds that are marked
                  as having sustainability mandates by Morningstar
                </div>
              </Col>
              <Col xs={12} sm={4} md={3}>
                <div styleName='good-fund-counts-result-and-link-wrap'>
                  <div styleName={'good-fund-counts-value'+sriGreen}>
                    {fundFamily.num_e_sri}
                    {
                      fundFamily.num_e_sri === 0 ?
                        <small>&nbsp;funds</small> :
                        null
                    }
                  </div>
                  {
                    fundFamily.num_e_sri > 0 ?
                      (
                        <Link
                          to={sriUrl}
                          title={
                            'Sustainability-mandated ' + fundFamily.fund_family_label +
                              ' funds'
                          }
                          xstrong
                        >
                          See funds<LinkIcon />
                        </Link>
                      ) :
                      null
                  }
                </div>
              </Col>
            </Row>
          </div>
          <div styleName='good-fund-counts-col'>
            <Row>
              <Col xs={12} sm={8} md={9}>
                <div styleName='good-fund-counts-title'>
                  "A grade" fossil free funds
                </div>
                <div styleName='good-fund-counts-info'>
                  Number of {fundFamily.fund_family_label} funds that earn a
                  fossil fuel grade of A
                </div>
              </Col>
              <Col xs={12} sm={4} md={3}>
                <div styleName='good-fund-counts-result-and-link-wrap'>
                  <div styleName={'good-fund-counts-value'+badgersGreen}>
                    {fundFamily.num_five_badgers}
                    {
                      fundFamily.num_five_badgers === 0 ?
                        <small>&nbsp;funds</small> :
                        null
                    }
                  </div>
                  {
                    fundFamily.num_five_badgers > 0 ?
                      (
                        <Link
                          to={badgerUrl}
                          title={
                            'A grade ' + fundFamily.fund_family_label + ' funds'
                          }
                          xstrong
                        >
                          See funds<LinkIcon />
                        </Link>
                      ) :
                      null
                  }
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    )
  }
}


/**
 * <FamilyResultsSectorDetails
 *   type
 *   label
 *   sublabel
 *   count
 *   weight
 *   asset
 *   currency
 * />
 *
 * Displays details for a fossil fuel sector, like Carbon Underground 200.
 * Depending on the sector, displays a different icon. Displays label,
 * sublabel, count, weight, asset provided by props.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyResultsSectorDetails extends Component {
  constructor(props) {
    super(props)
    this.state = {
      type: this.props.type || null,
      label: this.props.label || null,
      sublabel: this.props.sublabel || null,
      count: this.props.count || null,
      weight: this.props.weight || null,
      asset: this.props.asset || null,
      currency: this.props.currency || null,
    }
  }

  render() {
    const {
      type,
      label,
      sublabel,
      count,
      weight,
      asset,
      currency
    } = this.state

    // circle icon
    const iconNormal = require( '../../../static/images/icn-'+type+'.svg' )
    // circle icon inactive
    const iconClean = require( '../../../static/images/icn-'+type+'-inactive.svg' )
    // clean or dirty?
    const isDirty = (convert.int(count))
    // what style?
    let currIcon = null
    let cleanDirtyStyle = ''

    if (isDirty) {
      cleanDirtyStyle = 'detail-sector-'+type+'-active'
      currIcon = iconNormal
    } else {
      cleanDirtyStyle = 'detail-sector-clean'
      currIcon = iconClean
    }

    return (
      <div styleName='detail-sector-wrapper'>
        <div styleName={cleanDirtyStyle}>
          <div styleName='detail-sector-detail-icon-wrap'>
            <img
              styleName='detail-icon'
              src={currIcon}
              alt={label}
            />
          </div>
          <div styleName={'detail-title-active'}>
            <div><b>{label}</b></div>
            {
              sublabel ?
                (
                  <div styleName='detail-sublabel'>{sublabel}</div>
                ) :
                null
            }
          </div>
          {
            isDirty ?
              (
                <div styleName='detail-sector-stat-wrapper'>

                  <div styleName='detail-stat-active'>
                    <div styleName='detail-stat-figure'>
                      {format.number(count)}
                    </div>
                    <div styleName='detail-stat-label'>holdings</div>
                  </div>
                  <div styleName='detail-stat-active'>
                    <div styleName='detail-stat-figure'>
                      {format.money(asset, true, currency, true)}
                    </div>
                    <div styleName='detail-stat-label'>invested</div>
                  </div>
                  <div styleName='detail-stat-active'>
                    <div styleName='detail-stat-figure'>
                      {format.perc(weight)}
                    </div>
                    <div styleName='detail-stat-label'>of fund assets</div>
                  </div>
                </div>
              ) :
              (
                <div styleName='single-badge-wrap'>
                  <div styleName='single-badge-text'>
                    <div styleName='single-badge-subtitle'>
                      No direct stock holdings
                    </div>
                  </div>
                </div>
              )
          }
        </div>
      </div>
    )
  }
}


/**
 * <FamilyBanner
 *   fundFamily
 * />
 *
 * Displays a banner for the fund family that has a different tagline if the
 * family is one of the 10 families with additional data in config, or just
 * a generic fund family.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyBanner extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundFamily: props.fundFamily || null
    }
  }

  render() {
    const { fundFamily } = this.state
    if (!fundFamily) { return null }

    return (
      <div styleName='banner-wrap'>
        <div styleName='banner'>
          <div styleName='banner-inner'>
            <div styleName='banner-taglines'>
              <div>
                <div styleName='banner-tagline'>
                  {
                    fundFamily ?
                      (
                        <span>Are your retirement funds</span>
                      ) : (
                        <span>Your retirement plan could</span>
                      )
                  }
                </div>
              </div>
              <div>
                <div styleName='banner-tagline'>
                  {
                    fundFamily ?
                      (
                        <span>invested with {fundFamily.fund_family_label}?</span>
                      ) : (
                        <span>help fund climate solutions</span>
                      )
                  }
                </div>
              </div>
              <div styleName='toolkit'>
                <div styleName='toolkit-info'>
                  Check out our action toolkit and talk to your retirement
                  plan manager
                </div>
                <Link
                  to={config.routes.toolkit}
                  log={analytics.eventToToolkit()}
                  title='Fossil free action toolkit'>
                  <div styleName='toolkit-action'>
                    Action toolkit<LinkIcon />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}


/**
 * <FamilySummarySentence
 *   fundFamily
 * />
 *
 * Displays sentence for the fund family that names the fund company
 * at the top of the list of funds and shareclasses for this family.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilySummarySentence extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundFamily: props.fundFamily || null
    }
  }

  render() {
    const { fundFamily } = this.state
    if (!fundFamily) { return null }

    return (
      <div styleName='summary'>
        {fundFamily.fund_family_label} funds in our database
      </div>
    )
  }
}


/**
 * <FamilyFundList
 *   fundList
 * />
 *
 * Displays a list of funds for this family. Maps a fundList to a list of
 * FamilyFundListItem components.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyFundList extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fundList: props.fundList || null
    }
  }

  componentWillReceiveProps(props) {
    if (props.fundList) {
      this.setState({
        fundList: props.fundList
      })
    }
  }

  render() {
    const { fundList } = this.state
    if (!fundList) { return null }

    return (
      <div styleName='holding-list'>
        {
          fundList.map((fund, idx) => {
            return (
              <FamilyFundListItem
                fund={fund}
                key={'family-fund-list-item-'+idx}
              />
            )
          })
        }
      </div>
    )
  }
}


/**
 * <FamilyFundListItem
 *   fund
 * />
 *
 * Displays a fund in the list of funds for this family. Displays some summary
 * stats. Displays a list of tickers using the FamilyFundListItemTickerLinks
 * component.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyFundListItem extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fund: props.fund || null
    }
  }

  render() {
    const { fund } = this.state
    if (!fund) { return null }

    const {
      fund_legal_name,
      c2f5coogutcount,
      currency,
      c2f5coogutasset,
      c2f5coogutweight,
      // fund_id,
      grade_fossil
    } = fund

    return (
      <div styleName='holding'>
        <div styleName='ticker-name-assets'>
          <div styleName='ticker-name'>
            <div styleName='holdings-name'>
              {fund_legal_name}
            </div>
            <FamilyFundListItemTickerLinks shareclasses={fund.shareclass} />
          </div>
          <div styleName='ticker-assets'>
            <div styleName='ticker-assets-figure'>
              {format.number(c2f5coogutcount) }
            </div>
            <div styleName='ticker-assets-label'>
              &nbsp;fossil fuel stocks
            </div>
          </div>
          <div styleName='ticker-assets'>
            <div styleName='ticker-assets-figure'>
              {format.money(c2f5coogutasset, true, currency, true) }
            </div>
            <div styleName='ticker-assets-label'>
              &nbsp;invested
            </div>
          </div>
          <div styleName='ticker-assets'>
            <div styleName='ticker-assets-figure'>
              {format.perc(c2f5coogutweight) }
            </div>
            <div styleName='ticker-assets-label'>
              &nbsp;of portfolio
            </div>
          </div>
        </div>
        <div styleName={'score-wrapper'}>
          <Grade grade={grade_fossil} blocksmall/>
        </div>
      </div>
    )
  }
}


/**
 * <FamilyFundListItemTickerLinks
 *   shareclasses
 * />
 *
 * Displays a list of tickers, linked to those shareclass profiles, for a fund
 * in the fund list. Maps a list of shareclasses to a list of URLs and
 * tickers. Maps the list of URLs and tickers to a list of Links.
 *
 */
@CSSModules(Styles, {allowMultiple:true})
class FamilyFundListItemTickerLinks extends Component {
  constructor(props) {
    super(props)
    this.state = {
      shareclasses: props.shareclasses || null
    }
  }

  render() {
    const { shareclasses } = this.state
    if (!shareclasses) { return null }

    const tickerUrls = shareclasses.map((e,i)=>{
      const tckr = e.ticker || 'N/A'
      const safeTicker = e.ticker || 'N'
      const url = config.routes.fund + '/' +
        format.sanitize(e.fund_legal_name) + '/' + safeTicker + '/' +
        config.profilePath + '/' + e.fund_id + '/' + e.investment_vehicle_id

      return {
        ticker: tckr,
        name: e.name,
        url
      }
    })

    if (tickerUrls.length === 1 && tickerUrls[0].ticker === 'N/A') {
      const e = tickerUrls[0]
      return (
        <div styleName='ticker-links'>
            <Link to={e.url} title={e.name}>
              <span styleName='ticker'>See results<LinkIcon/></span>
            </Link>
        </div>
      )
    }

    return (
      <div styleName='ticker-links'>
        {
          tickerUrls.map((e, i)=>{
            return (
              <Link
                key={'holding-link-'+i}
                to={e.url}
                title={e.name}
              >
                { e.ticker === 'N/A' ?
                    <span styleName='ticker'>{e.name}</span> :
                    <span styleName='ticker'>{e.ticker}</span>
                }
              </Link>
            )
          })
        }
      </div>
    )
  }
}


/**
 * <Family />
 *
 * Connects to API and fetches a list of funds for this family.
 *
 */
@connect(
  state => ({
    result: state.familyShareClass.result,
    searching: state.familyShareClass.loading,
    searched: state.familyShareClass.loaded,
    error: state.familyShareClass.error,
    sector: state.sector.result
  }),
  searchActions)

@CSSModules(Styles, {allowMultiple:true})
export default class Family extends Component {
  static propTypes = {
    searching: PropTypes.bool,
    searched: PropTypes.bool,
    error: PropTypes.object,
    result: PropTypes.array,
  }
  static contextTypes =  {
    router: PropTypes.object.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      page: 1,
      family: props.location.query.q || '',
      searchQuery: '',
      lastResult: null,
      funds: [],
    }
  }

  componentWillMount() {
    if (typeof window !== 'undefined') {
      window.scrollTo(0,0)
    }
    this.familyShareClasses(this.state.family, 1)
  }

  componentWillReceiveProps(props) {
    const q = props.location.query.q || ''
    if (q !== this.state.family) {
      this.familyShareClasses(q, 1)
      window.scroll(0, 0)
      this.setState({
        page: 1,
        family: q,
        searchQuery: '',
        lastResult: null,
        funds: [],
      })
    } else if (props.result && this.state.lastResult !== props.result) {
      this.setState({
        lastResult: props.result,
        funds: this.state.funds.concat(props.result)
      })
    }
  }

  getMoreFunds = () => {
    this.familyShareClasses(this.state.family, this.state.page + 1)
  }

  familyShareClasses = (family,page) => {
    page = page || this.state.page
    this.setState({page:page})
    this.props.familyShareClasses(family, page)
  }

  configFundFamily = () => {
    return config.familyFunds.find(
      (family) => family.fund_family_label === this.state.family
    )
  }

  render() {
    if (!(this.state.page !== 1 || !this.props.searching)) {
      return (
        <div styleName='main'>
          <Loader
            options={{scale: 0.70, top:'2.0rem'}}
            loaded={ false }
          />
        </div>
      )
    }

    if (!this.props.location.query.q) {
      return (
        <div styleName='main-page'>
          <FamilyGrid fullGrid />
        </div>
      )
    }

    // redirect for SEO
    if (
      this.props.location.query.q.toUpperCase()==='BLACKROCK' ||
      this.props.location.query.q.toUpperCase()==='ISHARES'
    ) {
      this.context.router.push(
        config.routes.families + '?q=BlackRock%2FiShares'
      )
    }
    // redirect for TSP
    if (
      this.props.location.query.q.toUpperCase()==='THRIFT SAVINGS PLAN' ||
      this.props.location.query.q.toUpperCase()==='THRIFT%20SAVINGS%20PLAN'
    ) {
      this.context.router.push(config.routes.tspLanding)
    }

    const { funds } = this.state

    const fundFamily = this.configFundFamily()

    const fileName = format.imgPath(this.state.family)
    let hasLogo = false
    let companyLogo = null
    try {
      companyLogo = require('../../../static/images/fam/'+fileName+'.png')
      hasLogo = true
    }
    catch (e) {
      // console.log('')
    }

    const hasMore = (
      this.state.lastResult &&
      this.state.lastResult.length === config.queryMaxFunds
    )

    return (
      <div styleName='main'>
        {container.helmet(this.state.family)}
        <Loader
          options={{scale: 0.70, top: '2.0rem'}}
          loaded={ this.state.page !== 1 || !this.props.searching}
        >
          <div styleName='name-wrap-background'>
            <ContainerDivs>
                <div styleName='name-wrap'>
                  <div styleName='name'>
                    <div>
                      <div styleName={fundFamily ? 'name-tag-big' : 'name-tag'}>
                        {fundFamily ? 'Major fund manager' : 'Fund manager'}
                      </div>
                       <div styleName='name-logo-lbl'>
                        {
                          fundFamily ?
                            fundFamily.label :
                            this.state.family
                        }
                      </div>
                    </div>
                  </div>
                </div>
            </ContainerDivs>
          </div>

          { /* temporary asset manager letter for Vanguard */ }
          { /* <AssetManagerLetter fundFamilyName={this.state.family}/> */ }

          { /*
          < ContainerDivs>
            <FamilyResults fundFamily={fundFamily} />
          </ContainerDivs>
          */ }
          
          { /*
            fundFamily ?
              <FamilyBanner fundFamily={fundFamily}/> :
              null
          */ }

          <div styleName='holdings-wrapper'>
            <div styleName='holdings'>
              <FamilySummarySentence fundFamily={fundFamily}/>
              <div styleName='holdings-inner'>
                <div styleName='rating-label'>
                  <div styleName='rating-label-line'/>
                  <div styleName='rating-label-info'>
                    Fossil fuel grade
                  </div>
                </div>
                <FamilyFundList fundList={funds} />
              </div>
            </div>
          </div>

          <div styleName='more-funds-wrapper'>
            <Loader
              options={{scale: 0.70, top:'0'}}
              loaded={ !this.props.searching }
            />
            <div styleName='more-funds'>
              <div
                onClick={hasMore ? this.getMoreFunds : null}
                styleName={
                  hasMore ? 'more-funds-button' : 'no-more-funds-button'
                }
              >
                {
                  hasMore ?
                    'See more funds' :
                    'No more funds'
                }
                {
                  hasMore ?
                    <LinkIcon faclass='fa-plus-circle'/> :
                    null
                }
              </div>
              {
                !hasMore ?
                  (
                    <div styleName='no-more-funds-info'>
                      Looking for your favorite fund and can't find it? Sorry,
                      but we don't have everything in our database.
                    </div>
                  ) :
                  null
              }
            </div>
          </div>

          <GetStartedInput />

          {
            !fundFamily ?
              <FamilyBanner fundFamily={fundFamily}/> :
              null
          }

          <div styleName='signup-wrap'>
            <div styleName='signup-modal'>
              <AboutBanner />
            </div>
          </div>

        </Loader>
      </div>
    )
  }
}
